import React from 'react';
import {Card, Typography} from "@mui/material";
import {COLORS} from "../../../../ui/Theme";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {Box} from "@mui/system";
import {ListDivider} from "../../../../ui/ListStyles";
import useResponsive from "../../../../components/hooks/useResponsive";

const DerivativesStrategiesDetailsChart = ({title, data, baseEquidityLastPrice}) => {
    const {isSm} =  useResponsive();

    const sortedData = [...data].sort((a, b) => a.x - b.x);

    const CustomTooltip = ({active, payload}) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <Box
                    sx={{
                        backgroundColor: COLORS.SOFTBLUE,
                        padding: "10px",
                        borderRadius: "8px",
                        color: "#FFFFFF",
                        textAlign: "center",
                    }}
                >
                    <Typography sx={{fontSize: "12px", fontWeight: 700, color: COLORS.BLACK}}>
                        {new Intl.NumberFormat("fa-IR").format(data.x)}
                    </Typography>
                    <ListDivider/>
                    <Typography sx={{fontSize: "12px", fontWeight: 400, color: COLORS.BLUE}}>
                        {title}:
                        <span style={{fontWeight: 900}}> {new Intl.NumberFormat("fa-IR").format(data.y)}</span>
                    </Typography>
                </Box>
            );
        }

        return null;
    };

    const gradientOffset = () => {
        const dataMax = Math.max(...data.map((i) => i.y));
        const dataMin = Math.min(...data.map((i) => i.y));

        if (dataMax <= 0) {
            return 0;
        }
        if (dataMin >= 0) {
            return 1;
        }

        return dataMax / (dataMax - dataMin);
    };

    const off = gradientOffset();

    return (
        <Grid2 xs={12} md={6}>
            <Card
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    border: "1px solid #E0E0E0",
                    height: "400px",
                }}
            >
                <Typography sx={{fontSize: {xs: "18px", md: "24px"}, fontWeight: 700, color: COLORS.BLACK}}>
                    {title}
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={sortedData}
                        syncId="strategies"
                        margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
                    >
                        <defs>
                            <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                                <stop offset={off} stopColor={COLORS.GREEN} stopOpacity={0.4} />
                                <stop offset={off} stopColor={COLORS.RED} stopOpacity={0.4} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid stroke={COLORS.SOFTGRAY} strokeDasharray="0 0"/>
                        <XAxis
                            textAnchor="middle"
                            dataKey="x"
                            type="number"
                            domain={[
                                Math.min(...data.map((i) => i.x)),
                                Math.max(...data.map((i) => i.x)),
                            ]}
                            tickFormatter={(value) =>
                                new Intl.NumberFormat("fa-IR").format(value)
                            }
                            tickCount={data.length}
                            angle={-25}
                            tickLine={false}
                            tickMargin={10}
                            stroke={COLORS.GRAY}
                        />
                        <YAxis
                            textAnchor="start"
                            type="number"
                            tickFormatter={(tick) =>
                                new Intl.NumberFormat("fa-IR").format(tick)
                            }
                            tickMargin={10}
                            stroke={COLORS.GRAY}
                            angle={-25}
                            tickLine={false}
                            tick={{
                                fontSize: 14,
                                fontWeight: 400,
                            }}
                        />
                        <Tooltip cursor={{ fill: COLORS.SOFTGRAY }} content={<CustomTooltip/>}/>
                        <Legend
                            height={80}
                            verticalAlign="top"
                            align="right"
                            iconSize={10}
                            wrapperStyle={{
                                padding: "10px",
                                position: "absolute",
                                top: -10,
                                right: 0,
                                zIndex: 1,
                            }}
                            payload={[
                                {
                                    value: title,
                                    type: "circle",
                                    color: COLORS.BLUE,
                                },
                                {
                                    value: "آخرین سهم",
                                    type: "circle",
                                    color: COLORS.ORANGE,
                                },
                            ]}
                            content={({payload}) => (
                                <ul style={{
                                    display: "flex",
                                    gap: "10px",
                                    listStyleType: "none",
                                    margin: 0,
                                    padding: 0
                                }}>
                                    {payload.map((entry, index) => (
                                        <li
                                            key={`item-${index}`}
                                            style={{
                                                display: "flex",
                                                gap: "6px",
                                                alignItems: "center",
                                                marginBottom: "5px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "12px",
                                                    height: "12px",
                                                    borderRadius: "50%",
                                                    backgroundColor: entry.color,
                                                    marginRight: "10px",
                                                }}
                                            />
                                            <span
                                                style={{
                                                    color: entry.color,
                                                    fontSize: isSm ? "16px" : "12px",
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {entry.value}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        />
                        <ReferenceLine
                            x={baseEquidityLastPrice}
                            stroke={COLORS.ORANGE}
                            strokeDasharray="0"
                            label={{
                                position: "top",
                                value: baseEquidityLastPrice,
                                fill: COLORS.ORANGE,
                                fontSize: 16,
                                fontWeight: 400,
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey="y"
                            name={title}
                            stroke={COLORS.BLUE}
                            fill="url(#splitColor)"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </Card>
        </Grid2>
    );
};

export default DerivativesStrategiesDetailsChart;